import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { PAGE, SettingsHeader } from './SettingsUI';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Plus, Edit2, Trash2, ChevronRight, X, Users, AlertTriangle, Check } from 'lucide-react';

const API = '/pay-groups';

// SACS pay points per level (award structure). Used to build the Level dropdown and
// the SCHADS rate reference grid. L2–L4 have 4 pay points; the rest have 3.
const SCHADS_LEVELS = {
  '1': ['1', '2', '3'],
  '2': ['1', '2', '3', '4'],
  '3': ['1', '2', '3', '4'],
  '4': ['1', '2', '3', '4'],
  '5': ['1', '2', '3'],
  '6': ['1', '2', '3'],
  '7': ['1', '2', '3'],
  '8': ['1', '2', '3'],
};
const SCHADS_LEVEL_OPTIONS = Object.entries(SCHADS_LEVELS)
  .flatMap(([lvl, pps]) => pps.map(pp => `${lvl}.${pp}`));

// Pick the currently-effective award rates (latest effective_date on/before today,
// else the newest available) and index them by "level.payPoint.employmentType".
function currentSchadsMap(rates) {
  if (!rates?.length) return { eff: null, map: {} };
  const dates = [...new Set(rates.map(r => String(r.effective_date).slice(0, 10)))].filter(Boolean).sort();
  const today = new Date().toISOString().slice(0, 10);
  const eff = dates.filter(d => d <= today).pop() || dates[dates.length - 1];
  const map = {};
  for (const r of rates) {
    if (String(r.effective_date).slice(0, 10) !== eff) continue;
    map[`${r.level}.${r.pay_point}.${r.employment_type}`] = parseFloat(r.hourly_rate);
  }
  return { eff, map };
}

const DOW_OPTIONS = [
  { value: 'weekday',       label: 'Weekdays - Mon Tue Wed Thu Fri' },
  { value: 'saturday',      label: 'Saturday' },
  { value: 'sunday',        label: 'Sunday' },
  { value: 'public_holiday',label: 'Public Holidays' },
];

const DOW_COLORS = {
  weekday:       'bg-blue-100 text-blue-700',
  saturday:      'bg-purple-100 text-purple-700',
  sunday:        'bg-orange-100 text-orange-700',
  public_holiday:'bg-red-100 text-red-700',
};

function fmt12(t) {
  if (!t) return '';
  const [hRaw] = t.split(':').map(Number);
  const h = hRaw === 24 ? 0 : hRaw;
  if (h === 0) return '12 am';
  if (h === 12) return '12 pm';
  return h < 12 ? `${h} am` : `${h - 12} pm`;
}

function fmtDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-');
}

// ── Group modal ───────────────────────────────────────────────────────────────
function GroupModal({ group, onClose, onSaved, schadsRates }) {
  const [name, setName] = useState(group?.name || '');
  const [description, setDescription] = useState(group?.description || '');
  const [schadsLevel, setSchadsLevel] = useState(group?.schads_level || '');
  const [baseHourlyRate, setBaseHourlyRate] = useState(group?.base_hourly_rate ?? '');
  const [casualLoadingPct, setCasualLoadingPct] = useState(group?.casual_loading_pct ?? '');
  const [saving, setSaving] = useState(false);

  const { eff: rateEff, map: rateMap } = useMemo(() => currentSchadsMap(schadsRates), [schadsRates]);
  // The level is stored as "SW-2.1" by the org seeder — 87 of 90 pay groups platform-wide — while
  // this picker offered "Level 2.1". A <select> matches its options by exact string, so the level
  // read as "— None —" on almost every pay group in the product, and the award rate underneath it
  // never appeared because there was nothing selected to look up. Compare on the digits alone, and
  // write back the form the rest of the data already uses.
  const levelKey = (v) => { const m = /(\d+)\.(\d+)/.exec(v || ''); return m ? `${m[1]}.${m[2]}` : ''; };
  // Award rates for the selected "Level X.Y" (permanent + casual), if we have them.
  const award = useMemo(() => {
    const m = /(\d+)\.(\d+)/.exec(schadsLevel || '');
    if (!m) return null;
    const perm = rateMap[`${m[1]}.${m[2]}.permanent`];
    const casual = rateMap[`${m[1]}.${m[2]}.casual`];
    if (perm == null && casual == null) return null;
    return { perm, casual };
  }, [schadsLevel, rateMap]);

  function selectLevel(v) {
    setSchadsLevel(v ? `SW-${v}` : '');
    const m = /(\d+)\.(\d+)/.exec(v || '');
    const perm = m ? rateMap[`${m[1]}.${m[2]}.permanent`] : null;
    if (perm != null) setBaseHourlyRate(String(perm)); // auto-fill; still editable
  }

  async function handleSave() {
    if (!name.trim()) return toast.error('Name required');
    setSaving(true);
    try {
      const payload = { name, description, schadsLevel, baseHourlyRate, casualLoadingPct };
      if (group?.id) {
        const { data } = await api.put(`${API}/${group.id}`, payload);
        onSaved(data, false);
      } else {
        const { data } = await api.post(API, payload);
        onSaved(data, true);
      }
      toast.success(group?.id ? 'Pay group updated' : 'Pay group created');
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <h3 className="font-semibold text-slate-800">{group?.id ? 'Edit Pay Group' : 'New Pay Group'}</h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-slate-100 text-slate-400"><X size={18}/></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Name *</label>
            <input value={name} onChange={e => setName(e.target.value)}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="e.g. Default Casual"/>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Description</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              placeholder="Optional description"/>
          </div>
          {/* SCHADS Award rate — flows to staff payslips via the SCHADS pay calculator */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">SCHADS Level</label>
              <select value={levelKey(schadsLevel)} onChange={e => selectLevel(e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                <option value="">— None —</option>
                {SCHADS_LEVEL_OPTIONS.map(l => (
                  <option key={l} value={l}>Level {l}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Base Hourly Rate ($)</label>
              <input type="number" step="0.01" min="0" value={baseHourlyRate}
                onChange={e => setBaseHourlyRate(e.target.value)} placeholder="e.g. 34.85"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"/>
            </div>
          </div>
          {award && (
            <div className="flex items-center flex-wrap gap-1.5 -mt-1">
              <span className="text-[11px] text-slate-400">Award {rateEff ? `(${rateEff})` : ''}:</span>
              {award.perm != null && (
                <button type="button" onClick={() => setBaseHourlyRate(String(award.perm))}
                  className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 hover:bg-emerald-100">
                  Permanent ${award.perm.toFixed(2)}
                </button>
              )}
              {award.casual != null && (
                <button type="button" onClick={() => setBaseHourlyRate(String(award.casual))}
                  className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 hover:bg-amber-100">
                  Casual ${award.casual.toFixed(2)}
                </button>
              )}
              <span className="text-[11px] text-slate-400">— click to fill</span>
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Casual Loading (%)</label>
            <input type="number" step="0.1" min="0" value={casualLoadingPct}
              onChange={e => setCasualLoadingPct(e.target.value)} placeholder="e.g. 25"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"/>
            <p className="text-[11px] text-slate-400 mt-1">The base rate flows to staff payslips (SCHADS pay calculator) for staff assigned this pay group.</p>
          </div>
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-slate-100">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm border border-slate-200 hover:bg-slate-50">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="px-4 py-2 rounded-lg text-sm bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Rule modal ────────────────────────────────────────────────────────────────
function RuleModal({ rule, groupId, onClose, onSaved }) {
  const [form, setForm] = useState({
    dayOfWeek:     rule?.day_of_week     || 'weekday',
    timeStart:     rule?.time_start      || '06:00',
    timeEnd:       rule?.time_end        || '20:00',
    effectiveDate: rule?.effective_date  ? rule.effective_date.slice(0, 10) : '',
    code:          rule?.code            || '',
    multiplier:    rule?.multiplier      || '1',
  });
  const [saving, setSaving] = useState(false);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  async function handleSave() {
    setSaving(true);
    try {
      if (rule?.id) {
        const { data } = await api.put(`${API}/${groupId}/rules/${rule.id}`, form);
        onSaved(data, false);
      } else {
        const { data } = await api.post(`${API}/${groupId}/rules`, form);
        onSaved(data, true);
      }
      toast.success(rule?.id ? 'Rule updated' : 'Rule added');
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <h3 className="font-semibold text-slate-800">{rule?.id ? 'Edit Rule' : 'Add Rule'}</h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-slate-100 text-slate-400"><X size={18}/></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Day of Week *</label>
            <select value={form.dayOfWeek} onChange={set('dayOfWeek')}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
              {DOW_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Time Start</label>
              <input type="time" value={form.timeStart} onChange={set('timeStart')}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"/>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Time End</label>
              <input type="time" value={form.timeEnd === '24:00' ? '00:00' : form.timeEnd}
                onChange={e => setForm(f => ({ ...f, timeEnd: e.target.value === '00:00' ? '24:00' : e.target.value }))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"/>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Effective Date</label>
              <input type="date" value={form.effectiveDate} onChange={set('effectiveDate')}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"/>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Multiplier</label>
              <input type="number" step="0.01" min="0" value={form.multiplier} onChange={set('multiplier')}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"/>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Code</label>
            <input value={form.code} onChange={set('code')}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="e.g. Weekday Day 1"/>
          </div>
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-slate-100">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm border border-slate-200 hover:bg-slate-50">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="px-4 py-2 rounded-lg text-sm bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
// -- Award compliance panel ---------------------------------------------------
//
// When new SCHADS rates are imported the pay groups are deliberately left alone, so an
// organisation that pays above award keeps its own number. The side effect is that a group left
// on last year's figure keeps that too - and nothing on this screen said so, because the award
// rate only appeared inside the edit modal, one group at a time. Paying under the award is not a
// setting; it is an underpayment, so it is shown first and it says how many people it affects.
function AwardCheck({ check, onApplied }) {
  const [saving, setSaving] = useState(false);
  const [picked, setPicked] = useState(() => new Set());
  const below = useMemo(
    () => (check?.groups || []).filter(g => g.status === 'below_award'),
    [check]
  );

  // Every below-award group starts ticked: the default should be the fix, not the status quo.
  useEffect(() => { setPicked(new Set(below.map(g => g.id))); }, [below]);

  const zero = (check?.groups || []).filter(g => g.status === 'no_rate');

  const toggle = (id) => setPicked(p => {
    const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });

  async function apply() {
    const ids = [...picked];
    if (!ids.length) return;
    setSaving(true);
    try {
      const { data } = await api.post(`${API}/award-sync`, { ids });
      const n = data.updated?.length || 0;
      toast.success(n === 1
        ? `${data.updated[0].name} is now on the award rate`
        : `${n} pay groups are now on the award rate`);
      onApplied?.();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not update the rates');
    } finally { setSaving(false); }
  }

  if (!check) return null;

  if (!below.length) {
    if (!zero.length) return null;
    return (
      <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
        <div className="flex items-start gap-2.5">
          <AlertTriangle size={16} className="text-amber-600 mt-0.5 shrink-0" />
          <div className="text-sm text-amber-900">
            <span className="font-medium">{zero.length} pay group{zero.length === 1 ? '' : 's'} pay nothing.</span>
            {' '}{zero.map(g => g.name).join(', ')} {zero.length === 1 ? 'has' : 'have'} no SCHADS level and no
            hourly rate, so timesheets approved against {zero.length === 1 ? 'it' : 'them'} come out at $0.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-5 rounded-xl border border-red-200 bg-white overflow-hidden">
      <div className="px-4 py-3 bg-red-50 border-b border-red-100 flex items-start gap-2.5">
        <AlertTriangle size={16} className="text-red-700 mt-0.5 shrink-0" />
        <div className="min-w-0">
          <div className="text-sm font-semibold text-red-900">
            {below.length} pay group{below.length === 1 ? '' : 's'} pay below the SCHADS award
          </div>
          <div className="text-[13px] text-red-800 mt-0.5">
            {check.staffBelowAward > 0
              ? `${check.staffBelowAward} active staff member${check.staffBelowAward === 1 ? ' is' : 's are'} in ${below.length === 1 ? 'it' : 'them'}. `
              : 'No one is in them yet. '}
            Rates are checked against the award published for {check.effectiveDate || 'the current period'}.
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] text-slate-500 border-b border-slate-100">
              <th className="w-9 px-4 py-2"><span className="sr-only">Include</span></th>
              <th className="px-2 py-2 font-medium">Pay group</th>
              <th className="px-2 py-2 font-medium">Level</th>
              <th className="px-2 py-2 font-medium text-right">Pays now</th>
              <th className="px-2 py-2 font-medium text-right">Award</th>
              <th className="px-2 py-2 font-medium text-right">Short by</th>
              <th className="px-4 py-2 font-medium text-right">Staff</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {below.map(g => (
              <tr key={g.id} className={picked.has(g.id) ? '' : 'opacity-50'}>
                <td className="px-4 py-2">
                  <input type="checkbox" checked={picked.has(g.id)} onChange={() => toggle(g.id)}
                    aria-label={`Update ${g.name} to the award rate`}
                    className="rounded border-slate-300 text-primary-600 focus:ring-primary-500" />
                </td>
                <td className="px-2 py-2 text-slate-800">{g.name}</td>
                <td className="px-2 py-2 text-slate-500 text-[13px]">
                  {g.schadsLevel}{g.employmentType === 'casual' ? ' casual' : ''}
                </td>
                <td className="px-2 py-2 text-right tabular-nums text-slate-700">${Number(g.baseHourlyRate).toFixed(2)}</td>
                <td className="px-2 py-2 text-right tabular-nums text-slate-700">${Number(g.awardRate).toFixed(2)}</td>
                <td className="px-2 py-2 text-right tabular-nums font-medium text-red-800">
                  ${Math.abs(Number(g.variance)).toFixed(2)}
                </td>
                <td className="px-4 py-2 text-right tabular-nums text-slate-500">{g.staffCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between gap-3 flex-wrap">
        <p className="text-[12px] text-slate-500 max-w-[60ch]">
          Updating changes the rate from here on. Timesheets already approved keep the figure they
          were paid at, so any back pay owed is worked out separately.
        </p>
        <button type="button" onClick={apply} disabled={saving || !picked.size}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50 shrink-0">
          <Check size={15} />
          {saving ? 'Updating...' : `Update ${picked.size} to the award rate`}
        </button>
      </div>
    </div>
  );
}

// -- Penalty rates against the award ---------------------------------------------------
//
// The base rate is half of SCHADS; weekend, public holiday and shift loadings are the other half.
// Groups set up before the SCHADS template paid Sunday at 175% and public holidays at 225%, and
// none loaded a whole afternoon or night shift. Shown beside the base-rate check, fixed the same
// way: ticked by default, raise-only, and only when an admin presses the button.
function PenaltyCheck({ check, onApplied }) {
  const [saving, setSaving] = useState(false);
  const [picked, setPicked] = useState(() => new Set());
  const groups = useMemo(() => check?.groups || [], [check]);
  useEffect(() => { setPicked(new Set(groups.map(g => g.id))); }, [groups]);
  const toggle = (id) => setPicked(p => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const pct = (m, award) => (
    <span className={m < award ? 'font-medium text-red-800' : 'text-slate-600'}>{Math.round(m * 100)}%</span>
  );

  async function apply() {
    const ids = [...picked];
    if (!ids.length) return;
    setSaving(true);
    try {
      const { data } = await api.post(`${API}/penalty-sync`, { ids });
      const n = data.updated?.length || 0;
      toast.success(n === 1 ? `${data.updated[0].name} now pays the award penalties` : `${n} pay groups now pay the award penalties`);
      onApplied?.();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not update the penalty rates');
    } finally { setSaving(false); }
  }

  if (!groups.length) return null;
  return (
    <div className="mb-5 rounded-xl border border-amber-200 bg-white overflow-hidden">
      <div className="px-4 py-3 bg-amber-50 border-b border-amber-100 flex items-start gap-2.5">
        <AlertTriangle size={16} className="text-amber-700 mt-0.5 shrink-0" />
        <div className="min-w-0">
          <div className="text-sm font-semibold text-amber-900">
            {groups.length} pay group{groups.length === 1 ? '' : 's'} pay penalty rates below the SCHADS award
          </div>
          <div className="text-[13px] text-amber-800 mt-0.5">
            The award pays Saturday 150%, Sunday 200%, public holidays 250%, and a 12.5% afternoon or 15% night
            loading on the whole weekday shift (clauses 26, 29 and 34). Casuals get their 25% on top.
            {check.staff > 0 ? ` ${check.staff} active staff member${check.staff === 1 ? ' is' : 's are'} in these groups.` : ''}
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] text-slate-500 border-b border-slate-100">
              <th className="w-9 px-4 py-2"><span className="sr-only">Include</span></th>
              <th className="px-2 py-2 font-medium">Pay group</th>
              <th className="px-2 py-2 font-medium text-right">Saturday</th>
              <th className="px-2 py-2 font-medium text-right">Sunday</th>
              <th className="px-2 py-2 font-medium text-right">Public holiday</th>
              <th className="px-2 py-2 font-medium">Evening / night</th>
              <th className="px-4 py-2 font-medium text-right">Staff</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {groups.map(g => (
              <tr key={g.id} className={picked.has(g.id) ? '' : 'opacity-50'}>
                <td className="px-4 py-2">
                  <input type="checkbox" checked={picked.has(g.id)} onChange={() => toggle(g.id)}
                    aria-label={`Bring ${g.name} up to the award penalties`}
                    className="rounded border-slate-300 text-primary-600 focus:ring-primary-500" />
                </td>
                <td className="px-2 py-2 text-slate-800">{g.name}</td>
                <td className="px-2 py-2 text-right tabular-nums">{pct(g.saturday, 1.5)}</td>
                <td className="px-2 py-2 text-right tabular-nums">{pct(g.sunday, 2)}</td>
                <td className="px-2 py-2 text-right tabular-nums">{pct(g.publicHoliday, 2.5)}</td>
                <td className="px-2 py-2 text-[13px]">
                  {g.shiftLoading === 'schads_shift'
                    ? <span className="text-slate-600">Whole shift</span>
                    : <span className="font-medium text-red-800">Hours after 8pm only</span>}
                </td>
                <td className="px-4 py-2 text-right tabular-nums text-slate-500">{g.staffCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between gap-3 flex-wrap">
        <p className="text-[12px] text-slate-500 max-w-[60ch]">
          Only raises: a rate already above the award stays. Applies to timesheets approved from now on; anything
          already approved keeps what it was paid. If you pay through Xero, its pay items set the rates there.
        </p>
        <button type="button" onClick={apply} disabled={saving || !picked.size}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50 shrink-0">
          <Check size={15} />
          {saving ? 'Updating...' : `Bring ${picked.size} up to the award`}
        </button>
      </div>
    </div>
  );
}

// -- Missing award classifications ------------------------------------------------------
//
// The award table has every SACS classification; the seeded pay groups cover ten of them. A
// worker on 2.2 or 3.4 had no group to go on. This lists what is absent and adds it, priced from
// the award, with the penalty rules cloned from an existing SCHADS group.
function AwardFill({ data, onApplied }) {
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const missing = data?.missing || [];
  if (!data || !missing.length) return null;
  const perm = missing.filter(m => m.employmentType === 'permanent');
  const cas = missing.filter(m => m.employmentType === 'casual');

  async function apply() {
    setSaving(true);
    try {
      const { data: r } = await api.post(`${API}/award-fill`, {});
      toast.success(`${r.created?.length || 0} pay groups added${r.template ? `, penalty rules copied from ${r.template}` : ''}`);
      onApplied?.();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not add the pay groups'); }
    finally { setSaving(false); }
  }

  return (
    <div className="mb-5 rounded-xl border border-slate-200 bg-white overflow-hidden">
      <div className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-800">
            {missing.length} award classification{missing.length === 1 ? '' : 's'} have no pay group yet
          </div>
          <div className="text-[13px] text-slate-500 mt-0.5">
            {perm.length} full-time/part-time and {cas.length} casual. A worker on one of these has nothing to be put on.
            {' '}<button type="button" onClick={() => setOpen(o => !o)} className="underline hover:text-slate-700">{open ? 'Hide the list' : 'Show the list'}</button>
          </div>
        </div>
        <button type="button" onClick={apply} disabled={saving}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50 shrink-0">
          <Plus size={15} /> {saving ? 'Adding...' : `Add all ${missing.length} from the award`}
        </button>
      </div>
      {open && (
        <div className="px-4 pb-3 grid grid-cols-2 gap-x-6 text-[12px] text-slate-600">
          {[['Full-time / part-time', perm], ['Casual (rate includes the 25% loading)', cas]].map(([title, list]) => (
            <div key={title}>
              <div className="font-medium text-slate-700 mb-1">{title}</div>
              <div className="grid grid-cols-3 gap-y-0.5 tabular-nums">
                {list.map(m => <span key={`${m.level}.${m.payPoint}`}>{m.level}.{m.payPoint} — ${m.rate.toFixed(2)}</span>)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PayGroups() {
  const [groups, setGroups] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [groupModal, setGroupModal] = useState(null);
  const [ruleModal, setRuleModal] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [search, setSearch] = useState('');
  const [schadsRates, setSchadsRates] = useState([]);
  const [awardCheck, setAwardCheck] = useState(null);
  const [awardMissing, setAwardMissing] = useState(null);
  const [penaltyCheck, setPenaltyCheck] = useState(null);
  const loadPenaltyCheck = useCallback(async () => {
    try { const { data } = await api.get(`${API}/penalty-check`); setPenaltyCheck(data); }
    catch { /* non-fatal */ }
  }, []);
  const loadAwardMissing = useCallback(async () => {
    try { const { data } = await api.get(`${API}/award-missing`); setAwardMissing(data); }
    catch { /* non-fatal */ }
  }, []);

  const loadAwardCheck = useCallback(async () => {
    try {
      const { data } = await api.get(`${API}/award-check`);
      setAwardCheck(data);
    } catch { /* non-fatal - the screen still works without the check */ }
  }, []);

  const loadSchadsRates = useCallback(async () => {
    try {
      const { data } = await api.get(`${API}/schads-rates`);
      setSchadsRates(Array.isArray(data) ? data : []);
    } catch { /* non-fatal — modal shows an empty state */ }
  }, []);

  const loadGroups = useCallback(async () => {
    setLoadingGroups(true);
    try {
      const { data } = await api.get(API);
      setGroups(data);
      if (data.length && !selected) loadDetail(data[0].id);
    } catch { toast.error('Failed to load pay groups'); }
    finally { setLoadingGroups(false); }
  }, []);

  async function loadDetail(id) {
    setLoadingDetail(true);
    try {
      const { data } = await api.get(`${API}/${id}`);
      setSelected(data);
    } catch { toast.error('Failed to load pay group details'); }
    finally { setLoadingDetail(false); }
  }

  useEffect(() => { loadGroups(); loadSchadsRates(); loadAwardCheck(); loadAwardMissing(); loadPenaltyCheck(); }, [loadGroups, loadSchadsRates, loadAwardCheck, loadAwardMissing, loadPenaltyCheck]);

  async function handleDeleteGroup(id) {
    try {
      await api.delete(`${API}/${id}`);
      toast.success('Pay group deleted');
      setGroups(g => g.filter(x => x.id !== id));
      if (selected?.id === id) setSelected(null);
      setDeleteConfirm(null);
      loadAwardCheck();
    } catch (e) { toast.error(e.response?.data?.message || 'Delete failed'); }
  }

  async function handleDeleteRule(ruleId) {
    if (!selected) return;
    try {
      await api.delete(`${API}/${selected.id}/rules/${ruleId}`);
      toast.success('Rule deleted');
      setSelected(s => ({ ...s, rules: s.rules.filter(r => r.id !== ruleId) }));
    } catch (e) { toast.error(e.response?.data?.message || 'Delete failed'); }
  }

  function onGroupSaved(data, isNew) {
    if (isNew) {
      setGroups(g => [...g, { ...data, rule_count: 0 }]);
      loadDetail(data.id);
    } else {
      setGroups(g => g.map(x => x.id === data.id ? { ...x, ...data } : x));
      if (selected?.id === data.id) setSelected(s => ({ ...s, ...data }));
    }
    loadAwardCheck();
  }

  function onRuleSaved(data, isNew) {
    if (isNew) {
      setSelected(s => ({ ...s, rules: [...(s.rules || []), data] }));
      setGroups(g => g.map(x => x.id === selected.id ? { ...x, rule_count: Number(x.rule_count) + 1 } : x));
    } else {
      setSelected(s => ({ ...s, rules: s.rules.map(r => r.id === data.id ? data : r) }));
    }
  }

  const filtered = groups.filter(g => g.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className={PAGE}>
      <SettingsHeader
        title="Pay groups"
        description="Pay rate bands by day and time."
        actions={<button onClick={() => setGroupModal({})} className="btn-primary"><Plus size={15} /> New Pay Group</button>}
      />

      <AwardCheck check={awardCheck} onApplied={() => { loadGroups(); loadAwardCheck(); if (selected) loadDetail(selected.id); }} />
      <PenaltyCheck check={penaltyCheck} onApplied={() => { loadGroups(); loadPenaltyCheck(); if (selected) loadDetail(selected.id); }} />
      <AwardFill data={awardMissing} onApplied={() => { loadGroups(); loadAwardCheck(); loadAwardMissing(); }} />

      {/* Stacks on narrow screens; side by side from lg. */}
      <div className="flex flex-col gap-4 lg:flex-row">
        {/* Sidebar list */}
        <div className="w-full shrink-0 space-y-2 lg:w-64">
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search pay groups…"
            className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"/>
          {loadingGroups
            ? <div className="text-center py-8 text-slate-400 text-sm">Loading…</div>
            : filtered.length === 0
              ? <div className="text-center py-8 text-slate-400 text-sm">No pay groups</div>
              : filtered.map(g => (
                  <button key={g.id} onClick={() => loadDetail(g.id)}
                    className={`w-full text-left px-3 py-3 rounded-xl border transition-all ${
                      selected?.id === g.id
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-slate-200 bg-white hover:bg-slate-50'
                    }`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className={`text-sm font-medium ${selected?.id === g.id ? 'text-primary-700' : 'text-slate-700'}`}>{g.name}</div>
                        <div className="text-xs text-slate-400 mt-0.5">{g.rule_count} rules</div>
                      </div>
                      <ChevronRight size={14} className={selected?.id === g.id ? 'text-primary-500' : 'text-slate-300'}/>
                    </div>
                  </button>
                ))
          }
        </div>

        {/* Detail panel */}
        <div className="flex-1 min-w-0">
          {!selected ? (
            <div className="bg-white rounded-2xl border border-slate-200 flex flex-col items-center justify-center h-64 text-slate-400">
              <Users size={32} className="mb-2 opacity-30"/>
              <p className="text-sm">Select a pay group to view rules</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-200">
              {/* Group header */}
              <div className="flex items-center justify-between p-5 border-b border-slate-100">
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-semibold text-slate-800">{selected.name}</h2>
                    <button onClick={() => setGroupModal(selected)}
                      className="p-1 rounded hover:bg-slate-100 text-slate-400"><Edit2 size={14}/></button>
                  </div>
                  {selected.description && <p className="text-xs text-slate-500 mt-0.5">{selected.description}</p>}
                  <div className="flex items-center gap-2 mt-2">
                    {selected.schads_level && (
                      <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700">{selected.schads_level}</span>
                    )}
                    {selected.base_hourly_rate != null && (
                      <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
                        Base ${parseFloat(selected.base_hourly_rate).toFixed(2)}/hr
                      </span>
                    )}
                    {selected.casual_loading_pct != null && (
                      <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">
                        +{parseFloat(selected.casual_loading_pct)}% casual
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setRuleModal({})}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary-600 text-white text-sm hover:bg-primary-700">
                    <Plus size={14}/> Add Rule
                  </button>
                  <button onClick={() => setDeleteConfirm({ type: 'group', id: selected.id, name: selected.name })}
                    className="p-2 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500"><Trash2 size={15}/></button>
                </div>
              </div>

              {/* Rules table */}
              {loadingDetail ? (
                <div className="p-8 text-center text-slate-400 text-sm">Loading…</div>
              ) : !selected.rules?.length ? (
                <div className="p-8 text-center text-slate-400 text-sm">No rules yet — click Add Rule to get started</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-100">
                        <th className="text-left px-5 py-3 text-xs font-medium text-slate-500">Day of Week</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-500">Time</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-500">Effective Date</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-500">Code</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-500">Multiplier</th>
                        <th className="px-4 py-3"/>
                      </tr>
                    </thead>
                    <tbody>
                      {selected.rules.map(r => (
                        <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50/50 group">
                          <td className="px-5 py-3">
                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${DOW_COLORS[r.day_of_week] || 'bg-slate-100 text-slate-600'}`}>
                              {DOW_OPTIONS.find(o => o.value === r.day_of_week)?.label || r.day_of_week}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-slate-600">{fmt12(r.time_start)} – {fmt12(r.time_end)}</td>
                          <td className="px-4 py-3 text-slate-600">{r.effective_date ? fmtDate(r.effective_date) : '—'}</td>
                          <td className="px-4 py-3 font-medium text-slate-700">{r.code || '—'}</td>
                          <td className="px-4 py-3 text-slate-600">×{parseFloat(r.multiplier).toFixed(2)}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
                              <button onClick={() => setRuleModal(r)}
                                className="p-1.5 rounded hover:bg-blue-50 text-slate-400 hover:text-blue-600"><Edit2 size={13}/></button>
                              <button onClick={() => handleDeleteRule(r.id)}
                                className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-500"><Trash2 size={13}/></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Group modal */}
      {groupModal !== null && (
        <GroupModal group={groupModal?.id ? groupModal : null} schadsRates={schadsRates}
          onClose={() => setGroupModal(null)} onSaved={onGroupSaved}/>
      )}

      {/* Rule modal */}
      {ruleModal !== null && selected && (
        <RuleModal rule={ruleModal?.id ? ruleModal : null} groupId={selected.id} onClose={() => setRuleModal(null)} onSaved={onRuleSaved}/>
      )}

      {/* Delete confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <h3 className="font-semibold text-slate-800 mb-2">Delete Pay Group?</h3>
            <p className="text-sm text-slate-500 mb-5">
              "{deleteConfirm.name}" and all its rules will be permanently deleted.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 rounded-lg text-sm border border-slate-200 hover:bg-slate-50">Cancel</button>
              <button onClick={() => handleDeleteGroup(deleteConfirm.id)}
                className="px-4 py-2 rounded-lg text-sm bg-red-600 text-white hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
